import { IteroOrderAdditionalInfo } from 'components/ITeroOrderAdditionalInfo';
import { IteroPaymentType } from 'components/ITeroOrderPayment';
import { notifyError } from 'config/setupBugsnag';
import {
  Address,
  CreditCardClientData,
  CreditCardsResponse,
  IOrder,
  ITeroTaxClient,
  ITeroTaxServerResponse,
  ScannerFundingSource,
  ScannerSubFundingSource,
} from 'types';
import RestUtility from '../../../restUtility';
import {
  checkoutEndPoint,
  iteroEndpoint,
} from '../../../restUtility/restEndpoints';
import {
  getEuPaymentMethodsByFundingSource,
  getPaymentMethodsByFundingSource,
} from 'components/ITeroOrderPayment/ITeroOrderPayment.helper';
import getConfig from 'next/config';

export class IteroCheckoutService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async proceedToIteroCheckout(proposalId: string) {
    try {
      const response = await this.restUtility.post<IOrder>(
        `${iteroEndpoint}/checkout?proposal_id=${proposalId}`,
        {}
      );

      // return iteroCartService.buildCartForClient(response);
      return true;
    } catch (error) {
      notifyError('ApiError:: proceedToCheckout', error);
      throw error;
    }
  }

  async getDoctorPaymentOptions(
    paymentType: IteroPaymentType,
    tmSelectedScannerFundingSource: ScannerFundingSource,
    tmSelectedScannerSubFundingSource?: ScannerSubFundingSource,
    financingCompany?: string
  ) {
    const { publicRuntimeConfig } = getConfig();
    const storeRegion = publicRuntimeConfig.STORE_REGION?.toUpperCase();
    const isEMEA = storeRegion === 'EMEA';
    try {
      let paymentMethods: {
        cards: CreditCardClientData[];
        others: {
          label: string;
          value: ScannerSubFundingSource;
          logo?: string;
        }[];
      } = {
        cards: [],
        others: [],
      };
      const response = await this.restUtility.get<CreditCardsResponse>(
        `${checkoutEndPoint}/get_payment_cards?is_itero=true`
      );

      const creditCards = this.buildClientCreditCardsInfo(response);

      if (paymentType === 'subscription' || paymentType === 'rental') {
        paymentMethods.cards = creditCards;
      }

      if (paymentType === 'scanner') {
        if (isEMEA) {
          paymentMethods = getEuPaymentMethodsByFundingSource(
            tmSelectedScannerFundingSource,
            creditCards
          );
        } else {
          paymentMethods = getPaymentMethodsByFundingSource(
            tmSelectedScannerFundingSource,
            creditCards,
            tmSelectedScannerSubFundingSource,
            financingCompany
          );
        }
      }

      return paymentMethods;
    } catch (error) {
      notifyError('ApiError:: getDoctorCreditCards ', error);
      throw error;
    }
  }

  buildClientCreditCardsInfo(response: CreditCardsResponse) {
    const creditCards: CreditCardClientData[] = [];
    response.data.forEach(card => {
      const cardLastDigits = card.attributes.card_number.split('-')[1];
      const [expYear, expMonth] = card.attributes.expiry_date.split('-');
      const twoDigitExpYear = expYear.toString().substring(2);
      creditCards.push({
        id: card.id,
        default: card.attributes.default_card,
        cardType: card.attributes.card_type,
        expiry: `${expMonth}/${twoDigitExpYear}`,
        last4Digit: cardLastDigits,
        json_data: card.attributes.json_data,
        name: card.attributes.cardholder_name,
      });
    });
    return creditCards;
  }

  async placeIteroOrder(
    proposal_id: string,
    selectedShippingAddress: Address | undefined,
    selectedBillingAddress: Address | undefined,
    selectedSubFundingSource: ScannerSubFundingSource,
    selectedScannerPaymentCreditCard: CreditCardClientData | undefined,
    selectedSubscriptionPaymentCreditCard: CreditCardClientData | undefined,
    selectedRentalPaymentCreditCard: CreditCardClientData | undefined,
    additionalOrderInfo: IteroOrderAdditionalInfo,
    ipAddress: string,
    pcUsername: string | undefined,
    base64Stringpdf: string | undefined
  ) {
    try {
      let orderPayload;

      // eslint-disable-next-line prefer-const
      orderPayload = {
        proposal_id: proposal_id,
        bill_address_id: selectedBillingAddress?.salesforceId,
        ship_address_id: selectedShippingAddress?.salesforceId,
        sub_funding_source: selectedSubFundingSource,
        scanner_payment_token:
          selectedScannerPaymentCreditCard?.json_data?.ar_card_number,
        // pass the rental payment token in the subscription payment token for rental orders
        subscription_payment_token:
          selectedSubscriptionPaymentCreditCard?.json_data?.ar_card_number ??
          selectedRentalPaymentCreditCard?.json_data?.ar_card_number,
        additional_info: additionalOrderInfo,
        ip_address: ipAddress,
        pc_username: pcUsername,
        contract_document: base64Stringpdf,
      };
      console.log('orderPayload', orderPayload);

      const response = await this.restUtility.post<{
        success: boolean;
        data: { proposal: string };
      }>(`${iteroEndpoint}/complete`, orderPayload);

      return response;
    } catch (error) {
      notifyError('ApiError:: placeOrder', error);
      throw error;
    }
  }

  async calculateIteroTax(
    proposalId: string,
    billingAddressId: string,
    shippingAddressId: string
  ) {
    try {
      const response = await this.restUtility.post<ITeroTaxServerResponse>(
        `${iteroEndpoint}/calculate_tax`,
        {
          proposal_id: proposalId,
          bill_address_id: billingAddressId,
          ship_address_id: shippingAddressId,
        }
      );

      return this.buildIteroTax(response);
    } catch (error) {
      notifyError('ApiError:: calculateIteroTax', error);
      throw error;
    }
  }

  buildIteroTax(response: ITeroTaxServerResponse) {
    const tax: ITeroTaxClient = {
      displayTax: '',
      displayTotalAfterTax: '',
      estimatedMonthlyPayment: '',
    };

    tax.displayTax = response.data.display_tax_with_currency;
    tax.displayTotalAfterTax =
      response.data.display_total_after_tax_with_currency;
    tax.estimatedMonthlyPayment = response.data.estimated_monthly_payment;

    return tax;
  }
}
